export interface EditorJsBlock<Data = any> {
  id?: string;
  type: string;
  data: Data;
  /** Editor.js block tune payloads (e.g. `aiInstructions` string). */
  tunes?: Record<string, unknown>;
}

export interface EditorJsData {
  time?: number;
  blocks: EditorJsBlock[];
  version?: string;
}

export type SubpagesBlockData = {
  /**
   * Optional visual style for the subpages block.
   * This is intentionally minimal so existing documents remain compatible.
   */
  style?: 'list' | 'compact';
};

/** Document title (H1-equivalent); path/subpages render in the editor from live context, not stored. */
export type DocumentHeadingBlockData = {
  text: string;
};

export function normalizeDocumentHeadingBlockData(
  raw: unknown,
): DocumentHeadingBlockData {
  if (!raw || typeof raw !== 'object') return { text: '' };

  const rec = raw as Record<string, unknown>;
  const text = typeof rec.text === 'string' ? rec.text : '';
  return { text };
}

/** Legacy Editor.js block type removed in favor of the document heading portal. */
const LEGACY_SUBPAGES_BLOCK_TYPE = 'subpages';

/**
 * Older documents used Editor.js `header` with level 1 as the page title.
 * The app now uses `documentHeading` for that role (path portal, subpages, etc.).
 */
export function normalizeLeadingH1HeaderToDocumentHeading(
  data: EditorJsData,
): EditorJsData {
  const first = data.blocks[0];
  if (!first || first.type !== 'header') return data;

  const raw = first.data;
  if (!raw || typeof raw !== 'object') return data;

  const rec = raw as Record<string, unknown>;
  if (rec.level !== 1 || typeof rec.text !== 'string') return data;

  const nextFirst: EditorJsBlock<DocumentHeadingBlockData> = {
    type: 'documentHeading',
    data: { text: rec.text },
  };
  if (first.id) nextFirst.id = first.id;

  if (first.tunes) nextFirst.tunes = first.tunes;

  return {
    ...data,
    blocks: [nextFirst, ...data.blocks.slice(1)],
  };
}

/** Live uploads list; persisted payload is intentionally empty. */
export type FilesBlockData = Record<string, never>;

export type GalleryBlockItem = {
  url: string;
  caption?: string;
};

export type GalleryBlockData = {
  /** Number of images per row in the grid. Defaults to 3 when missing or invalid. */
  columnCount?: number;
  items: GalleryBlockItem[];
};

export const DEFAULT_GALLERY_COLUMN_COUNT = 3;
export const MAX_GALLERY_COLUMN_COUNT = 12;

function normalizeGalleryColumnCount(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return DEFAULT_GALLERY_COLUMN_COUNT;
  }

  const n = Math.floor(raw);
  if (n < 1) return DEFAULT_GALLERY_COLUMN_COUNT;

  if (n > MAX_GALLERY_COLUMN_COUNT) return MAX_GALLERY_COLUMN_COUNT;

  return n;
}

function getObjectValue(
  input: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined {
  const value = input[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function getStringValue(
  input: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

function normalizeGalleryItem(raw: unknown): GalleryBlockItem | null {
  if (typeof raw === 'string') {
    const url = raw.trim();
    return url ? { url } : null;
  }

  if (!raw || typeof raw !== 'object') return null;

  const rec = raw as Record<string, unknown>;
  const file = getObjectValue(rec, 'file');
  const image = getObjectValue(rec, 'image');
  const url =
    getStringValue(rec, ['url', 'src']) ??
    (file ? getStringValue(file, ['url', 'src']) : undefined) ??
    (image ? getStringValue(image, ['url', 'src']) : undefined);
  if (!url) return null;

  const caption = getStringValue(rec, ['caption', 'title', 'alt']);
  const item: GalleryBlockItem = { url };
  if (caption) {
    item.caption = caption;
  }

  return item;
}

/**
 * Coerces unknown JSON/editor state into a stable gallery block payload.
 */
export function normalizeGalleryBlockData(input: unknown): GalleryBlockData {
  if (!input || typeof input !== 'object') {
    return { columnCount: DEFAULT_GALLERY_COLUMN_COUNT, items: [] };
  }

  const rec = input as Record<string, unknown>;
  const nested = getObjectValue(rec, 'data') ?? getObjectValue(rec, 'config');
  const source = nested ?? rec;
  const itemsRaw = source.items;
  const items: GalleryBlockItem[] = [];
  if (Array.isArray(itemsRaw)) {
    for (const el of itemsRaw) {
      const item = normalizeGalleryItem(el);
      if (item) items.push(item);
    }
  }

  return {
    columnCount: normalizeGalleryColumnCount(
      source.columnCount ?? source.columns,
    ),
    items,
  };
}

export function parseEditorJs(
  value: string | null | undefined,
): EditorJsData | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as EditorJsData;
    if (!parsed || !Array.isArray(parsed.blocks)) return null;

    return {
      ...parsed,
      blocks: parsed.blocks.filter(
        (b) => b.type !== LEGACY_SUBPAGES_BLOCK_TYPE,
      ),
    };
  } catch {
    return null;
  }
}

export function stringifyEditorJs(
  data: EditorJsData | null | undefined,
): string {
  if (!data) {
    return JSON.stringify({ time: Date.now(), blocks: [], version: '2.0.0' });
  }

  return JSON.stringify(data);
}

/**
 * JSON for dirty-state comparison. Editor.js updates root `time` on `onChange`
 * even when block content is unchanged; omit it so caret moves do not look like edits.
 */
export function editorJsStableStringForDirtyCompare(
  data: EditorJsData | null | undefined,
): string {
  if (!data || !Array.isArray(data.blocks)) {
    return JSON.stringify({ blocks: [], version: '2.0.0' });
  }

  const { time: _ignoredTime, ...rest } = data;
  void _ignoredTime;
  return JSON.stringify(rest);
}

/**
 * Compares only block `type` + `data` for unsaved-document placeholder detection.
 * Strips Editor.js–assigned `id`, block-level `tunes`, and root `time` / `version`
 * so the live editor matches the seeded JSON until the user edits real content.
 */
export function editorJsStableStringForNewDocPlaceholderCompare(
  data: EditorJsData | null | undefined,
): string {
  if (!data || !Array.isArray(data.blocks)) {
    return JSON.stringify({ blocks: [] });
  }

  const { blocks } = data;
  const normalizedBlocks = blocks.map((block) => {
    const { id: _ignoredId, tunes: _ignoredTunes, ...blockRest } = block;
    void _ignoredId;
    void _ignoredTunes;
    return blockRest;
  });
  return JSON.stringify({ blocks: normalizedBlocks });
}
